import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormTextField, useZionFormContext, useFormState } from '@fs/zion-form'
import { Row, Cell, LabelSpacer } from '@fs/zion-ui'
import { useSlsStateContext, useSlsHighlightsContext, getNextUuid } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import HighlightButton from '../../panels/shared/HighlightButton'
import { prepareFieldForHighlights, handleInputBlur, handleInputFocus } from '../../shared/utils/highlightUtils'
import { validateLinkedEventFieldChange, getOriginalFormValue } from '../utils/events.utils'

/**
 * Generic SubElements renderer
 * Renders either a suggest component (passed via `SuggestComponent`) when
 * `fieldType === suggestFieldType` or a text field for other field types.
 *
 * @param {Object} props
 * @param {Array<{id: (string|number), fieldType: string, displayLabel?: string}>} props.subElements - Array of sub-element objects to render.
 * @param {number} props.eventIndex - Index of the event in the parent array.
 * @param {number} props.itemIndex - Index of the item in the parent array.
 * @param {string} props.indexName - Name of the index (e.g., 'primary', 'additional').
 * @param {{ primary: boolean }} props.field - Field object indicating if primary.
 * @param {function} props.t - Translation function.
 * @param {React.ComponentType} [props.SuggestComponent] - Component to render for suggest fields.
 * @param {string} [props.suggestFieldType] - Field type that triggers suggest rendering.
 * @param {string} [props.suggestLabelKey] - i18n key for suggest label.
 * @param {string} [props.suggestDefaultLabel] - Default label for suggest field.
 * @returns {React.ReactNode} Rendered sub-elements.
 */
const SubElements = ({
  subElements,
  eventIndex,
  itemIndex,
  indexName,
  field,
  t,
  SuggestComponent,
  suggestFieldType,
  suggestLabelKey,
  suggestDefaultLabel,
  isLinkedEvent = false,
  primaryEvent = false,
  splitScreenImageArk,
  onValidateFields,
}) => {
  const { getValues, control, resetField, setValue } = useZionFormContext()
  const { slsTypes, images } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const [tTranslation] = useTranslation()
  const { defaultValues } = useFormState({ control })
  const rip_sharedImageViewer_fullTextAttachFlow_v2 = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlow_v2')

  // Enhanced blur handler that triggers validation on both date and place fields
  const handleFieldBlur = useCallback(() => {
    // First, handle the highlight blur (existing behavior)
    handleInputBlur(h)

    // Then trigger validation on both date and place fields if callback provided
    if (onValidateFields) {
      onValidateFields()
    }
  }, [h, onValidateFields])

  // Handle highlight save and update form field with new shared field
  const handleHighlightSave = useCallback(
    (fieldName) => (data) => {
      // Update the field with the new shared field ID and value
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = getValues(fieldName)
        let newFieldValue = data.newFieldValue

        if (!['DATE', 'PLACE'].includes(currentFieldData?.fieldType)) {
          newFieldValue = {
            key: getNextUuid(),
            primaryText: data.newFieldValue,
            standard: null,
          }
        }

        resetField(fieldName, {
          defaultValue: {
            ...currentFieldData,
            id: data.newFieldId,
            formValue: newFieldValue,
            dispatches: data?.dispatches || [],
            locked: data?.selectedPersonIds?.length > 1, // Lock the field if it's associated with multiple persons
          },
          keepDirty: false,
          keepErrors: false,
          keepTouched: false,
        })

        // Ensure form value is updated immediately so the suggest input reflects the new value
        setValue(`${fieldName}.formValue`, newFieldValue, {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    },
    [resetField, setValue, getValues]
  )

  if (!subElements || subElements.length === 0) {
    return null
  }

  return subElements.map(
    ({ id, fieldType, displayLabel, typeData = { editable: true }, locked, added }, subElIndex) => {
      const basePath = `events.${field.primary ? 'primary' : 'additional'}[${eventIndex}].${indexName}[${itemIndex}].subElements[${subElIndex}]`

      if (fieldType === suggestFieldType && SuggestComponent) {
        const Suggest = SuggestComponent
        const suggestTestId = `${indexName.slice(0, -1)}-field`
        return (
          <Row gutters="xxs" key={`sub-${id || subElIndex}`}>
            <Suggest
              key={id}
              name={`${basePath}.formValue`}
              label={t(suggestLabelKey, suggestDefaultLabel)}
              data-testid={`${suggestTestId}-${itemIndex}`}
              warningValidate={(val) => {
                const originalValue = getOriginalFormValue(
                  defaultValues,
                  primaryEvent,
                  eventIndex,
                  indexName,
                  itemIndex
                )
                return validateLinkedEventFieldChange(isLinkedEvent, val, originalValue, t)
              }}
              standardize
              onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
              onBlur={handleFieldBlur}
              onSuggestionSelected={() => {
                if (onValidateFields) {
                  onValidateFields()
                }
              }}
              onClear={() => {
                if (onValidateFields) {
                  onValidateFields()
                }
              }}
              disabled={!typeData?.editable}
              helpMessage={
                rip_sharedImageViewer_fullTextAttachFlow_v2.isOn && locked
                  ? 'This field is associated with multiple names.'
                  : undefined
              }
              growX
              dense
            />
            <Cell>
              <LabelSpacer />
              <HighlightButton
                field={prepareFieldForHighlights(getValues, fieldType, basePath, slsTypes, images)}
                splitScreenImageArk={splitScreenImageArk}
                buttonProps={{ 'data-testid': `${indexName}-highlight-button` }}
                linkName={`essential-information-${indexName}-highlight`}
                disabled={!typeData?.editable}
                onSave={handleHighlightSave(`${basePath}`)}
                saveToSls={!added}
                growX
                iconOnly
              />
            </Cell>
          </Row>
        )
      }

      const textTestId = `${indexName.slice(0, -1)}-field`

      return (
        <Row gutters="xxs" key={`sub-${id || subElIndex}`}>
          <FormTextField
            key={id}
            name={`${basePath}.formValue.primaryText`}
            label={displayLabel}
            data-testid={textTestId}
            warningValidate={(val) => {
              const originalValue = getOriginalFormValue(
                defaultValues,
                field.primary,
                eventIndex,
                indexName,
                itemIndex,
                subElIndex
              )
              return validateLinkedEventFieldChange(isLinkedEvent, val, originalValue, tTranslation)
            }}
            onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
            onBlur={handleFieldBlur}
            disabled={!typeData?.editable}
            helpMessage={
              rip_sharedImageViewer_fullTextAttachFlow_v2.isOn && locked
                ? 'This field is associated with multiple names.'
                : undefined
            }
            growX
            dense
          />
          <Cell>
            <LabelSpacer />
            <HighlightButton
              field={prepareFieldForHighlights(getValues, fieldType, basePath, slsTypes, images)}
              splitScreenImageArk={splitScreenImageArk}
              buttonProps={{ 'data-testid': `${indexName}-highlight-button` }}
              linkName={`essential-information-${indexName}-highlight`}
              disabled={!typeData?.editable}
              onSave={handleHighlightSave(`${basePath}`)}
              saveToSls={!added}
              growX
              iconOnly
            />
          </Cell>
        </Row>
      )
    }
  )
}

SubElements.propTypes = {
  subElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fieldType: PropTypes.string.isRequired,
      displayLabel: PropTypes.string,
      typeData: PropTypes.shape({
        editable: PropTypes.bool,
      }),
    })
  ).isRequired,
  eventIndex: PropTypes.number.isRequired,
  itemIndex: PropTypes.number.isRequired,
  indexName: PropTypes.string.isRequired,
  field: PropTypes.shape({ primary: PropTypes.bool }).isRequired,
  t: PropTypes.func.isRequired,
  SuggestComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  suggestFieldType: PropTypes.string,
  suggestLabelKey: PropTypes.string,
  suggestDefaultLabel: PropTypes.string,
  isLinkedEvent: PropTypes.bool,
  onValidateFields: PropTypes.func,
}

export default SubElements
